import React from 'react';
import { Box, Typography, Stack, Chip, Skeleton } from '@mui/material';
import { ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import { colors, grey } from '../../styles/tokens';

/**
 * MobileList - Composant liste responsive pour mobile
 * Remplace les tables par des cards sur mobile pour une meilleure UX
 *
 * Props:
 * - items: Array d'objets a afficher
 * - renderItem: Function(item, index) => { primary, secondary, meta, chips, onClick }
 * - loading: Boolean pour afficher les skeletons
 * - emptyMessage: Message quand la liste est vide
 * - showChevron: Afficher la fleche a droite (default true si onClick)
 */
const MobileList = ({
  items = [],
  renderItem,
  loading = false,
  emptyMessage = 'Aucun element',
  showChevron = true,
}) => {
  // Loading state
  if (loading) {
    return (
      <Box>
        {[1, 2, 3].map((i) => (
          <Box
            key={i}
            sx={{
              py: 1.5,
              px: 0,
              borderBottom: `1px solid ${colors.border}`,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box flex={1}>
                <Skeleton width="60%" height={20} />
                <Skeleton width="40%" height={16} sx={{ mt: 0.5 }} />
              </Box>
              <Skeleton width={60} height={24} />
            </Stack>
          </Box>
        ))}
      </Box>
    );
  }

  // Empty state
  if (!items || items.length === 0) {
    return (
      <Box
        sx={{
          py: 4,
          textAlign: 'center',
          color: colors.textSecondary,
        }}
      >
        <Typography variant="body2">{emptyMessage}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {items.map((item, index) => {
        const rendered = renderItem(item, index);
        if (!rendered) return null;

        const {
          primary,
          secondary,
          tertiary,
          meta,
          chips = [],
          onClick,
          avatar,
        } = rendered;

        const hasAction = !!onClick;
        const isLast = index === items.length - 1;

        return (
          <Box
            key={item._id || item.id || index}
            onClick={onClick}
            sx={{
              py: 1.5,
              px: 0,
              borderBottom: isLast ? 'none' : `1px solid ${colors.border}`,
              cursor: hasAction ? 'pointer' : 'default',
              transition: 'background-color 0.15s ease',
              '&:active': hasAction ? {
                bgcolor: colors.bgHover,
              } : {},
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              {/* Avatar optionnel */}
              {avatar && (
                <Box sx={{ flexShrink: 0 }}>
                  {avatar}
                </Box>
              )}

              {/* Contenu principal */}
              <Box flex={1} minWidth={0}>
                {/* Ligne principale */}
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {primary}
                </Typography>

                {/* Ligne secondaire */}
                {secondary && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.textSecondary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'block',
                      mt: 0.25,
                    }}
                  >
                    {secondary}
                  </Typography>
                )}

                {/* Ligne tertiaire (optionnelle) */}
                {tertiary && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: grey[400],
                      fontSize: '0.6875rem',
                      display: 'block',
                      mt: 0.25,
                    }}
                  >
                    {tertiary}
                  </Typography>
                )}

                {/* Chips */}
                {chips.length > 0 && (
                  <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
                    {chips.map((chip, i) => (
                      <Chip
                        key={i}
                        label={chip.label}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.6875rem',
                          ...chip.sx,
                        }}
                      />
                    ))}
                  </Stack>
                )}
              </Box>

              {/* Meta info (date, count, etc.) */}
              {meta && (
                <Box sx={{ flexShrink: 0, textAlign: 'right' }}>
                  {typeof meta === 'string' ? (
                    <Typography
                      variant="caption"
                      sx={{ color: colors.textSecondary }}
                    >
                      {meta}
                    </Typography>
                  ) : (
                    meta
                  )}
                </Box>
              )}

              {/* Chevron */}
              {hasAction && showChevron && (
                <ChevronRightIcon
                  sx={{
                    fontSize: 20,
                    color: grey[400],
                    flexShrink: 0,
                  }}
                />
              )}
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
};

export default MobileList;
